import { stateToSlug } from "@shared/formats/state";

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  stateSlug: string;
  country: string;
}

interface ZippopotamResponse {
  places?: Array<{
    "place name": string;
    state: string;
    "state abbreviation": string;
    latitude: string;
    longitude: string;
  }>;
  "post code": string;
  country: string;
  "country abbreviation": string;
}

const geocodeCache = new Map<
  string,
  { result: GeocodeResult | null; expiresAt: number }
>();
const SUCCESS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const FAILURE_CACHE_TTL_MS = 60 * 60 * 1000;

function remember(cacheKey: string, result: GeocodeResult | null): GeocodeResult | null {
  const ttl = result ? SUCCESS_CACHE_TTL_MS : FAILURE_CACHE_TTL_MS;
  geocodeCache.set(cacheKey, { result, expiresAt: Date.now() + ttl });
  return result;
}

function toGeocodeResult(data: ZippopotamResponse): GeocodeResult | null {
  const place = data.places?.[0];
  if (!place) return null;
  const state = place["state abbreviation"] || place.state;
  return {
    latitude: parseFloat(place.latitude),
    longitude: parseFloat(place.longitude),
    city: place["place name"],
    state,
    stateSlug: stateToSlug(state),
    country: data["country abbreviation"],
  };
}

export async function geocodePostalCode(
  postalCode: string,
  countryCode: "us" | "ca" = "us",
): Promise<GeocodeResult | null> {
  const normalizedPostal = postalCode.replace(/\s+/g, "").toUpperCase();
  const cacheKey = `${countryCode}:${normalizedPostal}`;
  const cached = geocodeCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.result;

  try {
    const response = await fetch(
      `https://api.zippopotam.us/${countryCode}/${encodeURIComponent(normalizedPostal)}`,
      { next: { revalidate: 86400 } },
    );
    if (!response.ok) {
      console.warn("geocode.lookup_failed", { postalCode: normalizedPostal, countryCode, status: response.status });
      return remember(cacheKey, null);
    }
    const result = toGeocodeResult(await response.json());
    if (!result) console.warn("geocode.no_place_found", { postalCode: normalizedPostal, countryCode });
    return remember(cacheKey, result);
  } catch (error) {
    console.error("geocode.fetch_error", {
      error: error instanceof Error ? error.message : String(error),
      postalCode: normalizedPostal,
      countryCode,
    });
    return remember(cacheKey, null);
  }
}

export function clearGeocodeCache(): void {
  geocodeCache.clear();
}
